import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { cloneDeep } from 'lodash';
import { ChangeDetectorRef } from '@angular/core';
import { Company, JsonbType } from '@xilo-mono/form-contracts';

/*
  How to trigger the color picker modal:
  const dialogRef = this.dialog.open(ColorPickerModalComponent, {
    data: { color: '', name: '' },
    panelClass: 'custom-dialog-container'
  });
  dialogRef.afterClosed().subscribe((result) => {
    // listen for the result
  });
*/

interface Color {
  backgroundColor: string;
  textColor: string;
}

interface iLifecycle {
  color: string;
  name: string;
  pipelineId: number;
  isEnabled: boolean;
  isNewClient: boolean;
  isQuoted: boolean;
  isSold: boolean;
  isLost: boolean;
  stageConfig?: any;
  data?: JsonbType;
}

@Component({
  selector: 'xilo-mono-color-picker-modal',
  templateUrl: './color-picker-modal.component.html',
  styleUrls: ['./color-picker-modal.component.scss']
})
export class ColorPickerModalComponent implements OnInit {
  colors: Color[][] = [
    [
      { backgroundColor: '#5254F1', textColor: '#ffffff' },
      { backgroundColor: '#3382F5', textColor: '#ffffff' },
      { backgroundColor: '#48A08B', textColor: '#ffffff' },
      { backgroundColor: '#EF9536', textColor: '#111111' },
      { backgroundColor: '#D82F50', textColor: '#ffffff' },
      { backgroundColor: '#CECCCC', textColor: '#111111' }
    ],
    [
      { backgroundColor: '#DFDFFB', textColor: '#111111' },
      { backgroundColor: '#D4E7FD', textColor: '#111111' },
      { backgroundColor: '#D7EDE9', textColor: '#111111' },
      { backgroundColor: '#F9E2BE', textColor: '#111111' },
      { backgroundColor: '#F8D1DF', textColor: '#111111' },
      { backgroundColor: '#F5F5F5', textColor: '#111111' }
    ],
    [
      { backgroundColor: '#918FF3', textColor: '#111111' },
      { backgroundColor: '#81B8F8', textColor: '#111111' },
      { backgroundColor: '#76BFB4', textColor: '#111111' },
      { backgroundColor: '#F5BB6E', textColor: '#111111' },
      { backgroundColor: '#EE7DA1', textColor: '#111111' },
      { backgroundColor: '#E2E0E0', textColor: '#111111' }
    ],
    [
      { backgroundColor: '#4444C4', textColor: '#ffffff' },
      { backgroundColor: '#2969C1', textColor: '#ffffff' },
      { backgroundColor: '#3C8A77', textColor: '#111111' },
      { backgroundColor: '#DA8831', textColor: '#111111' },
      { backgroundColor: '#C52B48', textColor: '#ffffff' },
      { backgroundColor: '#B7B5B5', textColor: '#111111' }
    ],
    [
      { backgroundColor: '#27266E', textColor: '#ffffff' },
      { backgroundColor: '#133B6E', textColor: '#ffffff' },
      { backgroundColor: '#24584D', textColor: '#ffffff' },
      { backgroundColor: '#B46A2A', textColor: '#111111' },
      { backgroundColor: '#8A1B33', textColor: '#ffffff' },
      { backgroundColor: '#757575', textColor: '#ffffff' }
    ]
  ];
  defaultStageConfig = {
    client: {}
  }
  stageConfigEntities = [
    {
      name: 'client',
      fields: [
        { label: 'First Name', name: 'firstName', validation: 'None' },
        { label: 'Last Name', name: 'lastName', validation: 'None' },
        { label: 'Email', name: 'email', validation: 'None' },
        { label: 'Phone', name: 'phone', validation: 'None' },
        { label: 'Premium', name: 'premium', validation: 'None' },
        { label: 'Term', name: 'policyTerm', validation: 'None' },
        { label: 'Effective Date', name: 'policyStartDate', validation: 'None' },
        { label: 'xDate', name: 'policyExpirationDate', validation: 'None' }
      ]
    },
    {
      name: 'policy',
      fields: [
        { label: 'LOB', name: 'lob', validation: 'None' },
        { label: 'Term', name: 'term', validation: 'None' },
        { label: 'xDate', name: 'xDate', validation: 'None' },
        { label: 'Product', name: 'productLine', validation: 'None' },
        { label: 'Premium', name: 'totalPremium', validation: 'None' },
        { label: 'Effective Date', name: 'effectiveDate', validation: 'None' }
      ]
    }
  ];
  // Validation types available
  validationTypes = ['None', 'Required'];
  selectedColor: Color;
  isEnabled: boolean;
  name = '';
  initialValues: { color: string; name: string };
  pipelines = [];
  selectedPipelineId: number;
  type: 'new' | 'quoted' | 'sold' | 'lost' | null = null;
  // Tracks whether client or policy sections are expanded
  clientFields: any[] = [];
  customFields: any[] = [];
  policyFields: any[] = [];
  validationFieldsLoaded = false;
  selectedAEStage: string;
  company: Company;

  constructor(
    public dialogRef: MatDialogRef<ColorPickerModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { lifecycle: iLifecycle; action; pipelines: any[], aeStages: JsonbType[], company: Company },
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.company = this.data.company;
    this.selectedAEStage = this.data.lifecycle?.data?.appliedEpicStage;
    this.initialValues = cloneDeep(this.data.lifecycle);
    const colorsReduced = this.colors.reduce((acc, val) => acc.concat(val), []);
    const incomingSelectedColor = colorsReduced.find(color => color.backgroundColor === this.data.lifecycle?.color);
    if (incomingSelectedColor) {
      this.selectedColor = incomingSelectedColor;
    }
    if (this.data.lifecycle?.name) {
      this.name = this.data.lifecycle.name;
    }
    if (this.data.lifecycle?.pipelineId) {
      this.selectedPipelineId = +this.data.lifecycle.pipelineId;
    }
    this.isEnabled = this.data.lifecycle?.isEnabled ?? false;
    if (this.data.lifecycle?.isNewClient) this.type = 'new';
    if (this.data.lifecycle?.isQuoted) this.type = 'quoted';
    if (this.data.lifecycle?.isSold) this.type = 'sold';
    if (this.data.lifecycle?.isLost) this.type = 'lost';
    // Initialize stageConfigEntities based on incoming data

    // Add custom fields from company.data.customFields if they exist
    if (this.company?.data?.customFields) {
      const customFieldEntity = {
        name: 'custom',
        fields: this.company.data.customFields.map(field => {
          // Check both the field's required property and stageConfig validation
          const isRequiredFromField = !!field?.required;
          const isRequiredFromStageConfig = this.data.lifecycle.stageConfig?.custom?.[field.id]?.validation === 'Required';
          const fieldValidation = (isRequiredFromField || isRequiredFromStageConfig) ? 'Required' : 'None';
          
          return {
            label: field.label,
            name: field.id,
            validation: fieldValidation
          };
        })
      };
      this.stageConfigEntities.push(customFieldEntity);
    }

    setTimeout(() => {
      if (this.data.lifecycle?.stageConfig && this.data.lifecycle.stageConfig.client) {
        this.updateEntitiesFromStageConfig(this.data.lifecycle.stageConfig);
      } else {
        this.updateEntitiesFromStageConfig(this.defaultStageConfig);
      }
    }, 500);
  }

  selectColor(selectedColor) {
    this.selectedColor = selectedColor;
  }

  save(): void {
    if (!this.isValid()) return;
    const updatedStageConfig = this.reconstructStageConfig();
    this.dialogRef.close({
      data: {
        color: this.selectedColor.backgroundColor,
        fontColor: this.selectedColor.textColor,
        name: this.name,
        action: this.data.action,
        pipelineId: this.selectedPipelineId,
        isEnabled: this.isEnabled,
        isNewClient: this.type === 'new' ? true : false,
        isQuoted: this.type === 'quoted' ? true : false,
        isSold: this.type === 'sold' ? true : false,
        isLost: this.type === 'lost' ? true : false,
        stageConfig: updatedStageConfig,
        data: { appliedEpicStage: this.selectedAEStage },
      }
    });
  }

  private isValid() {
    return this.selectedColor?.backgroundColor && this.selectedColor?.textColor && this.name;
  }

  cancel(): void {
    this.dialogRef.close({ data: this.initialValues });
  }

  getOutsideCircleColor(rowIndex: number, innerIndex: number, color: Color) {
    if (this.selectedColor?.backgroundColor === color?.backgroundColor) {
      if (rowIndex === 1 || rowIndex === 2) {
        return `5px solid ${this.colors[0][innerIndex].backgroundColor}`;
      } else {
        return `5px solid ${color.backgroundColor}`;
      }
    }
  }

  private updateEntitiesFromStageConfig(stageConfig: any): void {
    for (const entityName of Object.keys(stageConfig)) {
      const entity = this.stageConfigEntities.find(e => e.name === entityName);
      if (entity) {
        for (const fieldName of Object.keys(stageConfig[entityName])) {
          const field = entity.fields.find(f => f.name === fieldName);
          if (field) {
            field.validation = 'Required'; // Mark as required if needed
          }
        }
      }
    }
    this.clientFields = this.stageConfigEntities.find(e => e.name === 'client')?.fields || [];
    this.customFields = this.stageConfigEntities.find(e => e.name === 'custom')?.fields || [];
    this.policyFields = this.stageConfigEntities.find(e => e.name === 'policy')?.fields || [];
    this.validationFieldsLoaded = true;
    this.cdr.detectChanges();
    this.cdr.markForCheck();
  }

  // Handle changes in validation selection
  onValidationChange(entityName: string, fieldName: string, validation: string): void {
    const entity = this.stageConfigEntities.find(e => e.name === entityName);
    if (entity) {
      const field = entity.fields.find(f => f.name === fieldName);
      if (field) {
        field.validation = validation;
      }
    }
  }

  // Reconstruct stageConfig from the updated entities array
  private reconstructStageConfig(): any {
    const stageConfig = {};
    for (const entity of this.stageConfigEntities) {
      // Create entity object if it has any 'Required' fields
      const requiredFields = entity.fields.filter(f => f.validation === 'Required');
      if (requiredFields.length > 0) {
        stageConfig[entity.name] = {};
        for (const field of requiredFields) {
          // Map field name to corresponding validation type
          stageConfig[entity.name][field.name] = { validation: this.getValidationTypeForRequiredField(field.name) };
        }
      }
    }
    return stageConfig;
  }

  // Map fields to their specific validation type
  private getValidationTypeForRequiredField(fieldName: string): string {
    // Hard-coded validation mappings
    const validationMap = {
      email: 'EMAIL',
      phone: 'INTL_PHONE_NUMBER_FIX',
      premium: 'POSITIVE_NONZERO_DOUBLE',
      lastName: 'NON_EMPTY_STR',
      firstName: 'NON_EMPTY_STR',
      policyTerm: 'POSITIVE_NONZERO_INT',
      policyStartDate: 'DATE_CONVERTABLE',
      policyExpirationDate: 'DATE_CONVERTABLE',
      lob: 'NON_EMPTY_STR',
      data: 'JSON_OBJECT',
      term: 'POSITIVE_NONZERO_INT',
      xDate: 'DATE_CONVERTABLE',
      productLine: 'NON_EMPTY_STR',
      totalPremium: 'POSITIVE_NONZERO_DOUBLE',
      effectiveDate: 'DATE_CONVERTABLE'
    };
    return validationMap[fieldName] || 'Required';
  }
}
